import React from 'react';
import { PageBanner } from '../components/PageBanner';
import { ContactCTA } from '../components/ContactCTA';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { MoveIcon, ShieldCheckIcon, ActivityIcon, ZapIcon, CheckCircleIcon, AccessibilityIcon } from 'lucide-react';
export function PhysicalTherapyPage() {
  const {
    ref: introRef,
    isVisible: isIntroVisible
  } = useScrollAnimation();
  const {
    ref: benefitsRef,
    isVisible: isBenefitsVisible
  } = useScrollAnimation();
  const {
    ref: detailsRef,
    isVisible: isDetailsVisible
  } = useScrollAnimation();
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useScrollAnimation();
  return <main data-id="element-1005">
      <PageBanner title="Physical Therapy" subtitle="Restoring mobility, strength, and independence through personalized rehabilitation." breadcrumbs={[{
      label: 'Services',
      path: '/services'
    }, {
      label: 'Physical Therapy',
      path: '/services/physical-therapy'
    }]} data-id="element-1006" />

      {/* Intro Section */}
      <section className="py-16 md:py-20 bg-white" data-id="element-1007">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1008">
          <div ref={introRef} className={`transition-all duration-1000 ease-out ${isIntroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-1009">
            <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6" data-id="element-1010">
              <MoveIcon className="h-4 w-4 text-primary" data-id="element-1011" />
              <span className="text-primary text-sm font-semibold" data-id="element-1012">
                Rehabilitation Services
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6 leading-tight" data-id="element-1013">
              Expert Physical Therapy in the Comfort of Your Home
            </h2>
            <div className="space-y-5 text-dark-muted text-lg leading-relaxed" data-id="element-1014">
              <p data-id="element-1015">
                Our physical therapy services are designed to help patients
                regain their strength, balance, and mobility following surgery,
                injury, or illness. We work closely with you and your physician
                to develop a customized rehabilitation plan that targets your
                specific recovery goals.
              </p>
              <p data-id="element-1016">
                By bringing professional therapy directly to your home, we
                eliminate the stress of travel and allow you to practice
                functional movements in the environment where you spend most of
                your time.
              </p>
            </div>

            {/* RN Badge */}
            <div className="mt-8 flex items-center bg-primary/5 border border-primary/10 rounded-xl p-5" data-id="element-1017">
              <ShieldCheckIcon className="h-10 w-10 text-primary mr-4 flex-shrink-0" data-id="element-1018" />
              <div data-id="element-1019">
                <h4 className="font-bold text-dark" data-id="element-1020">Licensed Therapists</h4>
                <p className="text-sm text-dark-muted" data-id="element-1021">
                  Our therapy programs are conducted by licensed physical
                  therapists and supervised by our clinical leadership.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-light-alt" data-id="element-1022">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1023">
          <div ref={benefitsRef} className={`transition-all duration-1000 ease-out ${isBenefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-1024">
            <div className="text-center mb-14" data-id="element-1025">
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4" data-id="element-1026">
                Benefits of In-Home Physical Therapy
              </h2>
              <div className="w-16 h-1.5 bg-secondary mx-auto mb-6 rounded-full" data-id="element-1027"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1028">
              {[{
              icon: AccessibilityIcon,
              title: 'Improved Mobility',
              desc: 'Targeted exercises to increase range of motion and ease of movement.'
            }, {
              icon: ShieldCheckIcon,
              title: 'Fall Prevention',
              desc: 'Balance training and home safety assessments to reduce the risk of falls.'
            }, {
              icon: ZapIcon,
              title: 'Pain Reduction',
              desc: 'Manual therapy and therapeutic exercises to manage and alleviate chronic pain.'
            }, {
              icon: ActivityIcon,
              title: 'Post-Surgical Recovery',
              desc: 'Specialized protocols to accelerate healing after orthopedic or cardiac procedures.'
            }].map((benefit, idx) => <div key={idx} className="bg-white rounded-2xl p-7 shadow-card hover:shadow-card-hover border border-gray-100 transition-all duration-300 transform hover:-translate-y-1 group" data-id="element-1029">
                  <div className="bg-primary/10 w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary transition-colors duration-300" data-id="element-1030">
                    <benefit.icon className="h-7 w-7 text-primary group-hover:text-white transition-colors duration-300" data-id="element-1031" />
                  </div>
                  <h3 className="text-lg font-bold text-dark mb-3" data-id="element-1032">
                    {benefit.title}
                  </h3>
                  <p className="text-dark-muted text-sm leading-relaxed" data-id="element-1033">
                    {benefit.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className="py-16 md:py-20 bg-white" data-id="element-1034">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1035">
          <div ref={detailsRef} className={`transition-all duration-1000 ease-out ${isDetailsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-1036">
            <h2 className="text-3xl font-bold text-dark mb-6" data-id="element-1037">
              Our Physical Therapy Services Include
            </h2>
            <p className="text-dark-muted text-lg leading-relaxed mb-8" data-id="element-1038">
              We provide a comprehensive range of therapeutic interventions to
              ensure you reach your maximum functional potential.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1039">
              {['Gait and balance training', 'Therapeutic exercise programs', 'Manual therapy techniques', 'Home safety evaluations', 'Assistive device training (walkers, canes)', 'Pain management education', 'Strength and endurance building', 'Post-operative rehabilitation'].map((item, idx) => <div key={idx} className="flex items-center p-4 bg-light-alt rounded-xl border border-gray-100" data-id="element-1040">
                  <CheckCircleIcon className="h-6 w-6 text-secondary mr-3 flex-shrink-0" data-id="element-1041" />
                  <span className="text-dark font-medium" data-id="element-1042">{item}</span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-light-alt" data-id="element-1043">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1044">
          <div ref={ctaRef} className={`bg-primary rounded-3xl p-10 md:p-14 text-center shadow-xl relative overflow-hidden transition-all duration-1000 ease-out ${isCtaVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'}`} data-id="element-1045">
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(#ffffff 2px, transparent 2px)',
            backgroundSize: '30px 30px'
          }} data-id="element-1046"></div>
            <div className="relative z-10" data-id="element-1047">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{
              fontFamily: "'Cinzel', serif"
            }} data-id="element-1048">
                Ready to Start Your Recovery?
              </h2>
              <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-1049">
                Contact us today to discuss your physical therapy needs and
                schedule an initial assessment with our licensed therapists.
              </p>
              <a href="#/contact" className="inline-block bg-white text-primary-dark hover:bg-light-alt px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1" data-id="element-1050">
                Contact Us Today
              </a>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA data-id="element-1051" />
    </main>;
}